import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Crown, Users, DollarSign, Search, Edit, Star, Settings, 
  Check, X, Save, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const TIER_COLORS = {
  free: 'bg-[#52525B] text-white',
  premium: 'bg-gradient-to-r from-[#FF2E63] to-[#7000FF] text-white',
  elite: 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black'
};

const TIER_ICONS = {
  free: Users,
  premium: Star,
  elite: Crown
};

const AdminMemberships = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState({});
  const [limits, setLimits] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newTier, setNewTier] = useState('free');
  const [duration, setDuration] = useState('30');
  const [saving, setSaving] = useState(false);
  const [savingFeature, setSavingFeature] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    fetchData();
  }, [filterTier]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, featuresRes] = await Promise.all([
        axios.get(`${API}/memberships/stats`),
        axios.get(`${API}/memberships/users${filterTier !== 'all' ? `?tier=${filterTier}` : ''}`),
        axios.get(`${API}/memberships/features`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setFeatures(featuresRes.data.features || {});
      setLimits(featuresRes.data.limits || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load membership data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setNewTier(user.membership_tier || 'free');
    setDuration('30');
    setShowEditModal(true);
  };

  const handleSaveMembership = async () => {
    if (!editingUser) return;
    setSaving(true);
    
    try {
      await axios.put(`${API}/memberships/user`, {
        user_id: editingUser.id,
        tier: newTier,
        duration_days: parseInt(duration)
      });
      toast.success(`Membership updated to ${newTier}`);
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update membership');
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureToggle = async (featureKey, tier, currentValue) => {
    setSavingFeature(featureKey);
    
    const updatedFeature = {
      feature_key: featureKey,
      free: tier === 'free' ? !currentValue : features[featureKey].free,
      premium: tier === 'premium' ? !currentValue : features[featureKey].premium,
      elite: tier === 'elite' ? !currentValue : features[featureKey].elite
    };

    try {
      await axios.put(`${API}/memberships/features`, updatedFeature);
      
      // Update local state
      setFeatures(prev => ({
        ...prev,
        [featureKey]: {
          ...prev[featureKey],
          [tier]: !currentValue
        }
      }));
      
      toast.success(`${features[featureKey].name} updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feature');
    } finally {
      setSavingFeature(null);
    }
  };

  const handleLimitChange = async (tier, limitKey, value) => {
    try {
      await axios.put(`${API}/memberships/limits`, {
        tier,
        [limitKey]: parseInt(value)
      });
      
      setLimits(prev => ({
        ...prev,
        [tier]: {
          ...prev[tier],
          [limitKey]: parseInt(value)
        }
      }));
      
      toast.success(`${tier} ${limitKey.replace(/_/g, ' ')} updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update limit');
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    return photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`;
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.username?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.display_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-memberships-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Membership Management</h1>
        <p className="text-[#A1A1AA]">Manage user subscriptions and configure tier features</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#A1A1AA]" />
              <span className="text-[#A1A1AA]">Free Users</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.by_tier?.free || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-5 h-5 text-[#FF2E63]" />
              <span className="text-[#A1A1AA]">Premium</span>
            </div>
            <p className="text-2xl font-bold text-[#FF2E63]">{stats.premium_users || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-5 h-5 text-[#FFD700]" />
              <span className="text-[#A1A1AA]">Elite</span>
            </div>
            <p className="text-2xl font-bold text-[#FFD700]">{stats.elite_users || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-[#22c55e]" />
              <span className="text-[#A1A1AA]">Monthly Revenue</span>
            </div>
            <p className="text-2xl font-bold text-[#22c55e]">
              ${stats.monthly_revenue_estimate?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white/5 mb-6">
          <TabsTrigger 
            value="users" 
            data-testid="users-tab"
            className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger 
            value="features"
            data-testid="features-tab"
            className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
          >
            <Settings className="w-4 h-4 mr-2" />
            Feature Configuration
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                data-testid="search-users-input"
                className="pl-10 bg-white/5 border-white/10"
              />
            </div>
            <Select value={filterTier} onValueChange={val => { setFilterTier(val); setLoading(true); }}>
              <SelectTrigger data-testid="filter-tier-select" className="w-40 bg-white/5 border-white/10">
                <SelectValue placeholder="Filter by tier" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-[#A1A1AA] font-medium">User</th>
                    <th className="text-left p-4 text-[#A1A1AA] font-medium">Email</th>
                    <th className="text-left p-4 text-[#A1A1AA] font-medium">Tier</th>
                    <th className="text-left p-4 text-[#A1A1AA] font-medium">Expires</th>
                    <th className="text-right p-4 text-[#A1A1AA] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-[#A1A1AA]">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => {
                      const TierIcon = TIER_ICONS[user.membership_tier || 'free'];
                      return (
                        <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden">
                                {user.public_photos?.[0] ? (
                                  <img 
                                    src={getPhotoUrl(user.public_photos[0])} 
                                    alt="" 
                                    className="w-full h-full object-cover" 
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Users className="w-5 h-5 text-[#52525B]" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-medium">{user.display_name || user.username}</p>
                                <p className="text-xs text-[#52525B]">@{user.username}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-[#A1A1AA]">{user.email}</td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${TIER_COLORS[user.membership_tier || 'free']}`}>
                              <TierIcon className="w-3 h-3" />
                              {(user.membership_tier || 'free').charAt(0).toUpperCase() + (user.membership_tier || 'free').slice(1)}
                            </span>
                          </td>
                          <td className="p-4 text-[#A1A1AA]">
                            {user.membership_expires 
                              ? new Date(user.membership_expires).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="p-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                              className="text-[#A1A1AA] hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Feature Configuration Tab */}
        <TabsContent value="features" className="space-y-6">
          {/* Tier Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['free', 'premium', 'elite'].map(tier => {
              const Icon = TIER_ICONS[tier];
              const tierLimits = limits[tier] || {};
              
              return (
                <div key={tier} className={`rounded-xl p-4 ${TIER_COLORS[tier]}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-6 h-6" />
                    <h3 className="text-lg font-bold capitalize">{tier}</h3>
                  </div>
                  <div className="space-y-2 text-sm opacity-90">
                    <div className="flex justify-between">
                      <span>Grid Limit:</span>
                      <span>{tierLimits.grid_limit === 9999 ? 'Unlimited' : tierLimits.grid_limit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Taps/Day:</span>
                      <span>{tierLimits.taps_per_day === 9999 ? 'Unlimited' : tierLimits.taps_per_day}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Expiring Photos/Day:</span>
                      <span>{tierLimits.expiring_photos_per_day === 9999 ? 'Unlimited' : tierLimits.expiring_photos_per_day}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Configuration Table */}
          <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#FF2E63]" />
                Feature Access Configuration
              </h3>
              <p className="text-sm text-[#A1A1AA] mt-1">
                Check/uncheck to enable features for each membership tier
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-[#A1A1AA] font-medium">Feature</th>
                    <th className="text-center p-4 text-[#A1A1AA] font-medium w-24">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="w-4 h-4" />
                        Free
                      </div>
                    </th>
                    <th className="text-center p-4 text-[#A1A1AA] font-medium w-24">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="w-4 h-4 text-[#FF2E63]" />
                        Premium
                      </div>
                    </th>
                    <th className="text-center p-4 text-[#A1A1AA] font-medium w-24">
                      <div className="flex items-center justify-center gap-1">
                        <Crown className="w-4 h-4 text-[#FFD700]" />
                        Elite
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(features).map(([featureKey, feature]) => (
                    <tr key={featureKey} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{feature.name}</p>
                          <p className="text-xs text-[#52525B]">{feature.description}</p>
                        </div>
                      </td>
                      {['free', 'premium', 'elite'].map(tier => (
                        <td key={tier} className="p-4 text-center">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={feature[tier]}
                              onCheckedChange={() => handleFeatureToggle(featureKey, tier, feature[tier])}
                              disabled={savingFeature === featureKey}
                              data-testid={`feature-${featureKey}-${tier}`}
                              className={`
                                h-5 w-5 border-2
                                ${feature[tier] 
                                  ? 'bg-[#FF2E63] border-[#FF2E63] data-[state=checked]:bg-[#FF2E63]' 
                                  : 'border-white/20'
                                }
                              `}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tier Limits Configuration */}
          <div className="bg-[#111] border border-white/10 rounded-xl p-4 space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Info className="w-5 h-5 text-[#08D9D6]" />
              Usage Limits Configuration
            </h3>
            <p className="text-sm text-[#A1A1AA]">
              Set daily/monthly limits for each tier. Use 9999 for unlimited.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              {['free', 'premium', 'elite'].map(tier => {
                const tierLimits = limits[tier] || {};
                const Icon = TIER_ICONS[tier];
                
                return (
                  <div key={tier} className="space-y-4 p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-white font-semibold capitalize">
                      <Icon className="w-5 h-5" />
                      {tier}
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-[#A1A1AA] block mb-1">Grid Limit</label>
                        <Input
                          type="number"
                          value={tierLimits.grid_limit || 0}
                          onChange={(e) => handleLimitChange(tier, 'grid_limit', e.target.value)}
                          className="bg-white/5 border-white/10 h-9"
                          data-testid={`limit-${tier}-grid`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#A1A1AA] block mb-1">Taps per Day</label>
                        <Input
                          type="number"
                          value={tierLimits.taps_per_day || 0}
                          onChange={(e) => handleLimitChange(tier, 'taps_per_day', e.target.value)}
                          className="bg-white/5 border-white/10 h-9"
                          data-testid={`limit-${tier}-taps`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#A1A1AA] block mb-1">Expiring Photos/Day</label>
                        <Input
                          type="number"
                          value={tierLimits.expiring_photos_per_day || 0}
                          onChange={(e) => handleLimitChange(tier, 'expiring_photos_per_day', e.target.value)}
                          className="bg-white/5 border-white/10 h-9"
                          data-testid={`limit-${tier}-photos`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#A1A1AA] block mb-1">Super Taps/Month</label>
                        <Input
                          type="number"
                          value={tierLimits.super_taps_per_month || 0}
                          onChange={(e) => handleLimitChange(tier, 'super_taps_per_month', e.target.value)}
                          className="bg-white/5 border-white/10 h-9"
                          data-testid={`limit-${tier}-supertaps`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Edit Membership</DialogTitle>
          </DialogHeader>
          
          {editingUser && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-white/10 overflow-hidden">
                  {editingUser.public_photos?.[0] ? (
                    <img 
                      src={getPhotoUrl(editingUser.public_photos[0])} 
                      alt="" 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#52525B]" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">{editingUser.display_name || editingUser.username}</p>
                  <p className="text-sm text-[#A1A1AA]">{editingUser.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-[#A1A1AA]">Membership Tier</label>
                <Select value={newTier} onValueChange={setNewTier}>
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="edit-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium ($19.99/mo)</SelectItem>
                    <SelectItem value="elite">Elite ($39.99/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newTier !== 'free' && (
                <div className="space-y-2">
                  <label className="text-sm text-[#A1A1AA]">Duration (days)</label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="edit-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="180">180 days</SelectItem>
                      <SelectItem value="365">1 year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveMembership}
              disabled={saving}
              data-testid="save-membership-btn"
              className="bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              {saving ? 'Saving...' : 'Update Membership'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMemberships;
